import { ElementType, forwardRef, HTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import parse from 'html-react-parser'

import { removeParagraph } from '@/utils/strings'
import { isCorporate } from '@/constants'

import { cn } from '@/lib/utils'

const typographyVariants = cva('', {
  variants: {
    variant: {
      h1: 'font-oswald text-s-h1 md:text-d-h1 -mt-[2rem] -mb-[1.55rem] md:-mt-[1.75rem] md:-mb-[1.15rem] text-text-heading-light dark:text-text-heading-dark',
      h2: 'font-oswald text-s-h2 md:text-d-h2 -mt-[.65rem] -mb-[.25rem] md:-mt-[1.1rem] md:-mb-[.6rem] text-text-heading-light dark:text-text-heading-dark',
      h3: 'font-oswald text-s-h3 md:text-d-h3 -mt-[.85rem] -mb-[.65rem] md:-mt-[.9rem] md:-mb-[.45rem] text-text-heading-light dark:text-text-heading-dark',
      h4: 'font-oswald text-s-h4 md:text-d-h4 -mt-[.7rem] -mb-[.45rem] md:-mt-[.8rem] md:-mb-[.65rem] text-text-heading-light dark:text-text-heading-dark',
      h5: 'font-oswald text-s-h5 md:text-d-h5 -mt-[.55rem] -mb-[.35rem] md:-mt-[.7rem] md:-mb-[.45rem] text-text-heading-light dark:text-text-heading-dark',
      h6: 'font-oswald text-s-h6 md:text-d-h6 -mt-[1rem] -mb-[.9rem] md:-mt-[.6rem] md:-mb-[.35rem] text-text-heading-light dark:text-text-heading-dark',
      large:
        'font-roboto text-large -mt-[.5rem] -mb-[.6rem] text-text-primary-light dark:text-text-primary-dark',
      regular:
        'font-roboto text-regular -mt-[.4rem] -mb-[.5rem] text-text-primary-light dark:text-text-primary-dark',
      small:
        'font-roboto text-small -mt-[.3rem] -mb-[.5rem] text-text-primary-light dark:text-text-primary-dark',
      caption:
        'font-roboto font-medium text-caption -mt-[.4rem] -mb-[.5rem] text-text-primary-light dark:text-text-primary-dark',
      overline:
        'font-roboto font-medium text-overline -mt-[.3rem] -mb-[.4rem] text-text-primary-light dark:text-text-primary-dark',
      menu: 'font-roboto text-menu text-text-menu-light dark:text-text-menu-dark -mt-[.4rem] -mb-[.5rem]',
      'menu-mobile':
        'font-oswald text-d-h4 -mt-[.7rem] -mb-[.45rem] md:-mt-[.8rem] md:-mb-[.65rem] text-text-heading-light'
    }
  },
  defaultVariants: {
    variant: 'regular'
  }
})

const childrenClasses = cn([
  '[&_ul]:list-disc [&_ul]:pl-[1.45rem] [&_ul_br]:hidden [&_ul]:mt-[1.4rem]',
  `[&_u]:relative ${isCorporate ? '[&_u]:text-text-accent-light' : '[&_u]:text-[#0e7984]'} dark:[&_u]:text-text-accent-dark [&_u]:no-underline`,
  '[&_u]:after:absolute [&_u]:after:bottom-[0.12em] [&_u]:after:left-0 [&_u]:after:w-full [&_u]:after:h-[.2rem]',
  `${isCorporate ? '[&_u]:after:bg-text-accent-light' : '[&_u]:after:bg-[#0e7984]'} dark:[&_u]:after:bg-text-accent-dark`,
  '[&_u]:after:transition-transform [&_u]:after:duration-500 [&_u]:after:ease-in-out-cubic [&_u]:after:origin-left',
  '[&.reveal_u]:after:duration-0 [&.reveal_u]:after:scale-x-0',
  `[&_em]:not-italic ${isCorporate ? '[&_em]:text-text-accent-light dark:[&_em]:text-text-accent-dark' : '[&_em]:text-[#0e7984] dark:[&_em]:text-[#0e7984]'}`,
  `${isCorporate ? '[&_a]:text-text-accent-light' : '[&_a]:text-[#0e7984]'} dark:[&_a]:text-text-heading-invert-light`,
  '[&_.p-line]:block [&_.p-line]:mb-[1rem] [&_.p-line:last-child]:mb-0'
])

export type TypographyProps = HTMLAttributes<HTMLElement> &
  VariantProps<typeof typographyVariants> &
  Partial<{
    component: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'
    paragraph: boolean
    gradient: boolean
    asChild: boolean
  }>

const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant,
      className,
      children,
      component = false,
      paragraph = false,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Component = asChild
      ? Slot
      : ((component || (paragraph ? 'div' : 'span')) as ElementType)

    return (
      <Component
        className={cn(
          typographyVariants({ variant, className }),
          childrenClasses,
          ''
        )}
        ref={ref}
        data-typography
        {...props}
      >
        {parse(children ? removeParagraph(children as string) : '')}
      </Component>
    )
  }
)

export default Typography
